import { orgSelectors } from 'ducks/orgs'
import Alert from 'elements/Alert'
import { Severities } from 'elements/notificationVariants'
import { usePublicFeatureConfig } from 'hooks/usePublicFeatureConfig'
import { FC, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { OpenSolarThemeType } from 'Themes'
import { makeOpenSolarStyles } from 'themes/makeOpenSolarStyles'
import { useGetSupplierConfig } from '../hooks/useGetSupplierConfig'
import OrderLineItem from '../OrderLineItem'
import { HardwareSupplierFilterKeyType } from '../type'

export type CartDiscount = {
  discountName: string
  discountPercentage: number
  eligible: boolean
  notification: {
    text: string
    severity: Severities
  }
} | null

export const getCartDiscount = (
  lineItems: OrderLineItem[],
  supplier: HardwareSupplierFilterKeyType | undefined,
  callback: Function,
  context?: 'project' | 'cart'
) => {
  const cartDiscount: CartDiscount = null
  if (!lineItems.length || lineItems.some((lineItem) => lineItem.status === 'loading')) {
    callback(cartDiscount)
    return
  }

  callback(cartDiscount)
}

export const getCartDiscountValue = (initialCost: number, cartDiscount: CartDiscount): number => {
  return cartDiscount?.eligible && cartDiscount?.discountPercentage
    ? Math.round(initialCost * cartDiscount.discountPercentage) / 100
    : 0
}

const useNotificationStyles = makeOpenSolarStyles((theme: OpenSolarThemeType) => ({
  wrapper: {
    width: '100%',
    '& > *': {
      width: '100%',
    },
  },
}))

export const CartDiscountNotification: FC<{ lineItems: OrderLineItem[]; context?: 'project' | 'cart' }> = ({
  lineItems,
  context,
}) => {
  const classes = useNotificationStyles()
  const selectedSupplier = useSelector(orgSelectors.getSelectedHardwareSupplier)
  const [cartDiscount, setCartDiscount] = useState<CartDiscount>(null)

  useEffect(() => {
    if (selectedSupplier) {
      getCartDiscount(
        lineItems,
        selectedSupplier,
        (cartDiscount) => {
          setCartDiscount(cartDiscount)
        },
        context
      )
    }
  }, [lineItems, selectedSupplier])

  if (!cartDiscount || !selectedSupplier) return <></>

  return (
    <div className={classes.wrapper}>
      <Alert severity={cartDiscount.notification.severity}>{cartDiscount.notification.text}</Alert>
    </div>
  )
}

export const FeatureConfigDiscountNotification: FC = () => {
  const classes = useNotificationStyles()
  const selectedSupplier = useSelector(orgSelectors.getSelectedHardwareSupplier)
  const discountConfig = usePublicFeatureConfig('distributor_discounts', true)
  const { getSupplierConfig } = useGetSupplierConfig()

  if (!selectedSupplier) return <></>

  const distributorName = getSupplierConfig(selectedSupplier || '')?.name || ''
  const discounts = discountConfig?.[distributorName] || []

  return (
    <>
      {discounts.map((discount) => (
        <div className={classes.wrapper}>
          <Alert severity={'info'}>{discount.message}</Alert>
        </div>
      ))}
    </>
  )
}
